import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Target, CheckCircle2, TrendingUp, AlertCircle } from "lucide-react";

import { useDelayedFlag, useTran } from "@/hooks";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { useEsgDashboardStore } from "../../hooks";

const KpiOverviewCards = observer(() => {
  const { t } = useTran();
  const { summary, loading, ensureData } = useEsgDashboardStore();
  const showSkeleton = useDelayedFlag(loading);

  useEffect(() => {
    ensureData();
  }, [ensureData]);

  const cards = useMemo(
    () => [
      {
        title: t("Total Metrics"),
        value: summary.totalMetricsTracked,
        subtitle: t("{{count}} with targets", {
          count: summary.metricsWithTargets,
        }),
        icon: Target,
        accent: "text-blue-500",
        tint: "bg-blue-500/10",
      },
      {
        title: t("Completion Rate"),
        value: `${summary.overallCompletionRate}%`,
        subtitle: t("{{count}} with data", {
          count: summary.metricsWithData,
        }),
        icon: CheckCircle2,
        accent: "text-emerald-500",
        tint: "bg-emerald-500/10",
      },
      {
        title: t("On Track"),
        value: summary.metricsOnTrack,
        subtitle: t("Targets meeting goal"),
        icon: TrendingUp,
        accent: "text-green-500",
        tint: "bg-green-500/10",
      },
      {
        title: t("At Risk"),
        value: summary.metricsAtRisk,
        subtitle: t("Needs attention"),
        icon: AlertCircle,
        accent: "text-amber-500",
        tint: "bg-amber-500/10",
      },
    ],
    [summary, t],
  );

  if (showSkeleton) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={`summary-skeleton-${index}`}>
            <CardContent className="space-y-4 p-5">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-3 w-28" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card key={card.title} className="border border-border/80 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {card.title}
                  </p>
                  <p className="mt-2 text-3xl font-bold text-foreground">
                    {card.value}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {card.subtitle}
                  </p>
                </div>
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-radius ${card.tint}`}
                >
                  <Icon className={`h-5 w-5 ${card.accent}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
});

export default KpiOverviewCards;
